<template lang="pug">
#resident-group-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 宛先グループ情報修正

    .box-body.register-body
      FormInput(v-model="name" key-name="グループ名" name="groupname" type="text" placeholder="グループ名"
        :maxlength="32" :validation-rules="{ required: true, max: 32 }" :styles="{ width: '340px' }" required)
      FormLabel(key-name="種別" v-model="typeName")
      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmUpdateModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoResidentGroupList" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="宛先グループ情報を更新しますか？" @ok="updateResidentGroup")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoResidentGroupList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { SendMessageGroupListState, SendMessagesState } from "@/store/NeedRefreshes";

import { PcUrl } from "@/commons/constants/PcUrl";
import CustomValidate from "@/commons/utils/CustomValidate";
import SendMessageGroupsService, { SendMessageGroupsGetModel, GroupType, SendMessageGroupsPatchModel } from "@/commons/services/SendMessageGroups";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";

/**
 * 宛先グループ修正画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    ValidationAlert,
    FormLabel,
    FormInput
  },
  $_veeValidate: { validator: "new" }
})
export default class SendMessageGroupEdit extends Vue {
  /** 宛先グループサービス */
  @Inject("SendMessageGroupsService") sendMessageGroupsService!: SendMessageGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** グループ名 */
  name: string = "";
  /** 種別 */
  type: GroupType = GroupType.UNKNOWN;
  /** 種別名 */
  typeName: string = "";
  /** メンバー */
  members: Array<string> = [];
  /** 整合性チェック用の更新日時 */
  updated: number = 0;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle created
   */
  public created() {
    CustomValidate.addCustomValidate(this.$validator);

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    await this.createView();

    this.isBusy = false;

    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    const group: SendMessageGroupsGetModel = await this.getSendMessageGroup();
    this.name = group.name;
    this.type = group.type;
    this.members = group.members;
    this.updated = group.updated;

    if (group.type == GroupType.FAMILY) {
      this.typeName = "家族";
    } else if (group.type == GroupType.OTHER_USER) {
      this.typeName = "施設関係者";
    } else {
      this.typeName = "未設定";
    }

    return;
  }

  /**
   * ### 編集対象の宛先グループ取得
   */
  public async getSendMessageGroup(): Promise<SendMessageGroupsGetModel> {
    const response: SendMessageGroupsGetModel = await this.sendMessageGroupsService.get(userStore, Number(this.$route.params.id));
    return response;
  }

  /**
   * ### 更新確認ダイアログを表示する
   *
   * - バリデーションを実施する
   * - エラーでなければ確認ダイアログを表示する
   */
  public async showConfirmUpdateModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 宛先グループを更新する
   *
   * - ローディングを表示
   * - 宛先グループサービスを使用して宛先グループを更新する
   * - 例外が無ければ成功ダイアログを表示する
   */
  public async updateResidentGroup(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const groupId: number = Number(this.$route.params.id);
      const group: SendMessageGroupsPatchModel = this.createUpdateParam();
      await this.sendMessageGroupsService.patch(userStore, groupId, group);

      // 宛先グループ一覧を再読み込みさせるようstoreを更新
      let sendMessageGroupList: SendMessageGroupListState = needRefreshesStore.sendMessageGroupList;
      sendMessageGroupList.sendMessageGroupList = true;
      needRefreshesStore.setSendMessageGroupList(sendMessageGroupList);
      // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
      sendMessageState.sendMessages = true;
      needRefreshesStore.setSendMessage(sendMessageState);

      // 更新成功ダイアログを表示する
      this.showUpdateSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 宛先グループ更新パラメータ作成
   */
  public createUpdateParam(): SendMessageGroupsPatchModel {
    const group: SendMessageGroupsPatchModel = {
      name: this.$stringUtil.convertYen(this.name),
      type: this.type,
      members: this.members,
      updated: this.updated
    };

    return group;
  }

  /**
   * ### 宛先グループ一覧画面に遷移する
   */
  public gotoResidentGroupList(): void {
    this.$router.push(PcUrl.SendMessageGroupList);

    return;
  }
}
</script>

<style lang="scss" scoped>
#resident-group-edit {
  max-width: 740px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
